import { useEffect, useRef, useState } from 'react';
import { fitImageToPanel } from '../lib/geometry';
import type { SourceRotation } from '../types';

interface CropSaveData {
  cropX: number;
  cropY: number;
  rotation: SourceRotation;
  flipH: boolean;
  flipV: boolean;
}

interface Props {
  source: Blob;
  sourceWidth: number;
  sourceHeight: number;
  panelWidth: number;
  panelHeight: number;
  initialCropX: number;
  initialCropY: number;
  initialRotation: SourceRotation;
  initialFlipH: boolean;
  initialFlipV: boolean;
  label: string;
  onSave: (data: CropSaveData) => void;
  onCancel: () => void;
}

const MAX_DISPLAY_W = 640;
const MAX_DISPLAY_H = 520;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const rotateRight = (r: SourceRotation): SourceRotation =>
  ((r + 90) % 360) as SourceRotation;
const rotateLeft = (r: SourceRotation): SourceRotation =>
  ((r + 270) % 360) as SourceRotation;

export function CropModal({
  source,
  sourceWidth,
  sourceHeight,
  panelWidth,
  panelHeight,
  initialCropX,
  initialCropY,
  initialRotation,
  initialFlipH,
  initialFlipV,
  label,
  onSave,
  onCancel,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bitmapRef = useRef<ImageBitmap | null>(null);
  const [bitmapReady, setBitmapReady] = useState(false);
  const [cropX, setCropX] = useState(initialCropX);
  const [cropY, setCropY] = useState(initialCropY);
  const [rotation, setRotation] = useState<SourceRotation>(initialRotation);
  const [flipH, setFlipH] = useState(initialFlipH);
  const [flipV, setFlipV] = useState(initialFlipV);
  const [dragging, setDragging] = useState(false);

  const isQuarterTurn = rotation === 90 || rotation === 270;
  const orientedW = isQuarterTurn ? sourceHeight : sourceWidth;
  const orientedH = isQuarterTurn ? sourceWidth : sourceHeight;

  const scaleW = MAX_DISPLAY_W / orientedW;
  const scaleH = MAX_DISPLAY_H / orientedH;
  const scale = Math.min(scaleW, scaleH, 1);
  const dispW = Math.round(orientedW * scale);
  const dispH = Math.round(orientedH * scale);

  useEffect(() => {
    let cancelled = false;
    let createdBitmap: ImageBitmap | null = null;
    createImageBitmap(source)
      .then((bm) => {
        if (cancelled) {
          bm.close();
          return;
        }
        createdBitmap = bm;
        bitmapRef.current = bm;
        setBitmapReady(true);
      })
      .catch(() => {
        /* swallow */
      });
    return () => {
      cancelled = true;
      if (createdBitmap) createdBitmap.close();
      bitmapRef.current = null;
    };
  }, [source]);

  useEffect(() => {
    if (!bitmapReady) return;
    const canvas = canvasRef.current;
    const bitmap = bitmapRef.current;
    if (!canvas || !bitmap) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(dispW * dpr);
    canvas.height = Math.round(dispH * dpr);
    canvas.style.width = `${dispW}px`;
    canvas.style.height = `${dispH}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);

    ctx.clearRect(0, 0, orientedW, orientedH);

    // Draw the source with rotation + flip applied around the oriented center.
    ctx.save();
    ctx.translate(orientedW / 2, orientedH / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.drawImage(bitmap, -sourceWidth / 2, -sourceHeight / 2);
    ctx.restore();

    // Compute the crop rectangle in oriented coords.
    const fit = fitImageToPanel(orientedW, orientedH, panelWidth, panelHeight, cropX, cropY);

    // Dim everything outside the crop.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(0, 0, orientedW, fit.sy);
    ctx.fillRect(0, fit.sy + fit.sHeight, orientedW, orientedH - (fit.sy + fit.sHeight));
    ctx.fillRect(0, fit.sy, fit.sx, fit.sHeight);
    ctx.fillRect(fit.sx + fit.sWidth, fit.sy, orientedW - (fit.sx + fit.sWidth), fit.sHeight);

    // Bright frame outlining the crop.
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2 / scale;
    ctx.strokeRect(fit.sx + 1 / scale, fit.sy + 1 / scale, fit.sWidth - 2 / scale, fit.sHeight - 2 / scale);

    // Center crosshair (in oriented coords, so it tracks visual center).
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.55)';
    ctx.lineWidth = 1 / scale;
    const cx = fit.sx + fit.sWidth / 2;
    const cy = fit.sy + fit.sHeight / 2;
    ctx.beginPath();
    ctx.moveTo(cx - 8 / scale, cy);
    ctx.lineTo(cx + 8 / scale, cy);
    ctx.moveTo(cx, cy - 8 / scale);
    ctx.lineTo(cx, cy + 8 / scale);
    ctx.stroke();
  }, [
    bitmapReady,
    cropX,
    cropY,
    rotation,
    flipH,
    flipV,
    sourceWidth,
    sourceHeight,
    panelWidth,
    panelHeight,
    orientedW,
    orientedH,
    dispW,
    dispH,
    scale,
  ]);

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragging) return;
    e.preventDefault();
    const fit = fitImageToPanel(orientedW, orientedH, panelWidth, panelHeight, cropX, cropY);
    const availSrcX = orientedW - fit.sWidth;
    const availSrcY = orientedH - fit.sHeight;
    const dCropX = availSrcX > 0 ? (e.movementX / scale) / availSrcX : 0;
    const dCropY = availSrcY > 0 ? (e.movementY / scale) / availSrcY : 0;
    setCropX((v) => clamp01(v + dCropX));
    setCropY((v) => clamp01(v + dCropY));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    setDragging(false);
  };

  const resetAll = () => {
    setCropX(0.5);
    setCropY(0.5);
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
  };

  const handleSave = () => {
    onSave({ cropX, cropY, rotation, flipH, flipV });
  };

  const fitInfo = fitImageToPanel(orientedW, orientedH, panelWidth, panelHeight, cropX, cropY);
  const hasRoom = orientedW - fitInfo.sWidth > 1 || orientedH - fitInfo.sHeight > 1;
  const dragHint = hasRoom
    ? 'Drag the highlighted area to reposition. Use the transform buttons to rotate or flip the source.'
    : "Your image fills the card exactly — rotate/flip still work, but there's nothing to drag.";

  const rotationLabel = rotation === 0 ? '' : ` · rotated ${rotation}°`;
  const flipsLabel =
    flipH && flipV ? ' · flipped both' : flipH ? ' · flipped H' : flipV ? ' · flipped V' : '';
  const isPristine =
    cropX === 0.5 && cropY === 0.5 && rotation === 0 && !flipH && !flipV;

  return (
    <div className="crop-overlay" role="dialog" aria-modal="true" onClick={onCancel}>
      <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="crop-close"
          onClick={onCancel}
          aria-label="Cancel"
          title="Cancel"
        >
          ✕
        </button>

        <h3 className="crop-title">Edit image — {label}</h3>
        <p className="crop-meta">{dragHint}</p>

        <div className="crop-canvas-wrap" style={{ width: dispW, height: dispH }}>
          {!bitmapReady && <div className="crop-loading">Loading image…</div>}
          <canvas
            ref={canvasRef}
            className="crop-canvas"
            style={{ cursor: dragging ? 'grabbing' : 'grab' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
        </div>

        <div className="crop-transforms" role="toolbar" aria-label="Image transforms">
          <button
            type="button"
            className="crop-tx-btn"
            onClick={() => setRotation(rotateLeft(rotation))}
            title="Rotate 90° counter-clockwise"
          >
            ↺ Rotate left
          </button>
          <button
            type="button"
            className="crop-tx-btn"
            onClick={() => setRotation(rotateRight(rotation))}
            title="Rotate 90° clockwise"
          >
            ↻ Rotate right
          </button>
          <span className="crop-tx-sep" aria-hidden>·</span>
          <button
            type="button"
            className={`crop-tx-btn${flipH ? ' active' : ''}`}
            onClick={() => setFlipH((v) => !v)}
            title="Mirror across the vertical axis"
            aria-pressed={flipH}
          >
            ⇋ Flip H
          </button>
          <button
            type="button"
            className={`crop-tx-btn${flipV ? ' active' : ''}`}
            onClick={() => setFlipV((v) => !v)}
            title="Mirror across the horizontal axis"
            aria-pressed={flipV}
          >
            ⇅ Flip V
          </button>
        </div>

        <div className="crop-readout">
          Source <strong>{sourceWidth}×{sourceHeight}</strong>{rotationLabel}{flipsLabel}
          {' '}→ cropped to <strong>{fitInfo.sWidth}×{fitInfo.sHeight}</strong>
          {' '}at ({Math.round(cropX * 100)}%, {Math.round(cropY * 100)}%)
        </div>

        <div className="crop-actions">
          <button
            type="button"
            className="crop-btn-secondary"
            onClick={resetAll}
            disabled={isPristine}
          >
            Reset all
          </button>
          <div className="crop-actions-right">
            <button type="button" className="crop-btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="crop-btn-primary"
              onClick={handleSave}
              disabled={!bitmapReady}
            >
              Save crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
